import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import TelegramLoginButton from './TelegramLoginButton';

function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [authMode, setAuthMode] = useState('password'); // 'password' | 'magic_link'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const { signIn, signUp, signInWithOAuth, signInWithMagicLink, signInWithTelegramData } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      if (authMode === 'magic_link') {
        await signInWithMagicLink(email);
        setMessage('Check your email for the sign-in link!');
      } else if (isLogin) {
        await signIn(email, password, rememberMe);
      } else {
        await signUp(email, password);
        setMessage('Account created! Please check your email for verification.');
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider) => {
    setError(null);
    setMessage(null);

    try {
      await signInWithOAuth(provider, rememberMe);
    } catch (err) {
      setError(err.message || `Failed to sign in with ${provider}`);
    }
  };

  const handleTelegramAuth = async (telegramUser) => {
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      await signInWithTelegramData(telegramUser);
    } catch (err) {
      setError(err.message || 'Telegram login failed');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#FAFAFA',
      padding: '20px',
    },
    card: {
      width: '100%',
      maxWidth: '420px',
      padding: '40px',
      borderRadius: '20px',
      backgroundColor: 'white',
      boxShadow: '0 0 0 1px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.04)',
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#111827',
      marginBottom: '8px',
      textAlign: 'center',
      letterSpacing: '-0.025em',
    },
    subtitle: {
      fontSize: '14px',
      color: '#A1A1AA',
      marginBottom: '28px',
      textAlign: 'center',
      letterSpacing: '-0.011em',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    label: {
      fontSize: '13px',
      fontWeight: '500',
      color: '#374151',
      letterSpacing: '-0.011em',
    },
    input: {
      padding: '11px 14px',
      fontSize: '15px',
      border: '1px solid #E0E0E0',
      borderRadius: '10px',
      outline: 'none',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    },
    button: {
      padding: '12px',
      fontSize: '15px',
      fontWeight: '600',
      border: 'none',
      borderRadius: '14px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginTop: '4px',
      letterSpacing: '-0.011em',
    },
    primaryButton: {
      backgroundColor: '#3b82f6',
      color: 'white',
    },
    disabledButton: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    toggle: {
      textAlign: 'center',
      marginTop: '16px',
      fontSize: '13px',
      color: '#A1A1AA',
    },
    toggleLink: {
      color: '#3b82f6',
      cursor: 'pointer',
      fontWeight: '500',
      textDecoration: 'none',
    },
    error: {
      padding: '12px 14px',
      borderRadius: '10px',
      backgroundColor: '#FEE2E2',
      color: '#991B1B',
      fontSize: '13px',
      letterSpacing: '-0.011em',
    },
    success: {
      padding: '12px 14px',
      borderRadius: '10px',
      backgroundColor: '#D1FAE5',
      color: '#065F46',
      fontSize: '13px',
      letterSpacing: '-0.011em',
    },
    loader: {
      display: 'inline-block',
      width: '16px',
      height: '16px',
      border: '2px solid #ffffff',
      borderTop: '2px solid transparent',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
      marginRight: '8px',
    },
    divider: {
      display: 'flex',
      alignItems: 'center',
      margin: '24px 0',
      color: '#A1A1AA',
      fontSize: '13px',
    },
    dividerLine: {
      flex: 1,
      height: '1px',
      backgroundColor: '#E8E8E8',
    },
    dividerText: {
      padding: '0 14px',
    },
    oauthButton: {
      padding: '12px',
      fontSize: '14px',
      fontWeight: '500',
      border: '1px solid #E0E0E0',
      borderRadius: '14px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginBottom: '12px',
      letterSpacing: '-0.011em',
    },
    oauthButtonGoogle: {
      color: '#111827',
    },
    modeToggle: {
      textAlign: 'center',
      marginTop: '8px',
      fontSize: '13px',
    },
  };

  const getSubtitle = () => {
    if (authMode === 'magic_link') return 'Sign in with a magic link';
    return isLogin ? 'Sign in to your account' : 'Create a new account';
  };

  const getButtonText = () => {
    if (loading) return 'Processing...';
    if (authMode === 'magic_link') return 'Send Magic Link';
    return isLogin ? 'Sign In' : 'Sign Up';
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '4px' }}>
          <img src={process.env.PUBLIC_URL + '/zenith-logo.png'} alt="Zenith" style={{ width: '38px', height: '38px', borderRadius: '10px' }} />
          <h1 style={{ ...styles.title, marginBottom: 0 }}>Zenith Expense</h1>
        </div>
        <p style={{ fontSize: '13px', color: '#A1A1AA', textAlign: 'center', margin: '10px 0 16px', lineHeight: '1.6', letterSpacing: '-0.011em' }}>
          AI-powered expense tracker. Scan receipts, import bank statements, categorize spending, and get insights — all in one place.
        </p>
        <p style={styles.subtitle}>{getSubtitle()}</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={styles.input}
              required
              disabled={loading}
            />
          </div>

          {authMode === 'password' && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={styles.input}
                required
                disabled={loading}
                minLength={6}
              />
            </div>
          )}

          {authMode === 'password' && isLogin && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '4px'
            }}>
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: '#3b82f6'
                }}
              />
              <label
                htmlFor="rememberMe"
                style={{
                  fontSize: '13px',
                  color: '#71717A',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Keep me signed in for 7 days
              </label>
            </div>
          )}

          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          {message && (
            <div style={styles.success}>
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              ...styles.primaryButton,
              ...(loading ? styles.disabledButton : {}),
            }}
          >
            {loading && <span style={styles.loader}></span>}
            {getButtonText()}
          </button>

          {isLogin && (
            <div style={styles.modeToggle}>
              {authMode === 'password' ? (
                <span
                  style={styles.toggleLink}
                  onClick={() => { setAuthMode('magic_link'); setError(null); setMessage(null); }}
                >
                  Sign in with email link instead
                </span>
              ) : (
                <span
                  style={styles.toggleLink}
                  onClick={() => { setAuthMode('password'); setError(null); setMessage(null); }}
                >
                  Sign in with password
                </span>
              )}
            </div>
          )}
        </form>

        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <div style={styles.dividerText}>Or continue with</div>
          <div style={styles.dividerLine}></div>
        </div>

        <button
          onClick={() => handleOAuthSignIn('google')}
          disabled={loading}
          style={{
            ...styles.oauthButton,
            ...styles.oauthButtonGoogle,
            ...(loading ? styles.disabledButton : {}),
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
            <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.01a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
            <path fill="#FBBC05" d="M4.5 10.52A4.8 4.8 0 0 1 4.5 7.5V5.42H1.83a8 8 0 0 0 0 7.17l2.67-2.07z"/>
            <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.42L4.5 7.5a4.77 4.77 0 0 1 4.48-3.32z"/>
          </svg>
          Continue with Google
        </button>

        <TelegramLoginButton
          botName={process.env.REACT_APP_TELEGRAM_BOT_NAME}
          onAuth={handleTelegramAuth}
          buttonSize="large"
          cornerRadius={8}
          disabled={loading}
        />

        <div style={styles.toggle}>
          {authMode === 'magic_link' ? (
            <>
              {'Want to use a password? '}
              <span
                style={styles.toggleLink}
                onClick={() => {
                  setAuthMode('password');
                  setIsLogin(true);
                  setError(null);
                  setMessage(null);
                }}
              >
                Sign In
              </span>
              {' or '}
              <span
                style={styles.toggleLink}
                onClick={() => {
                  setAuthMode('password');
                  setIsLogin(false);
                  setError(null);
                  setMessage(null);
                }}
              >
                Sign Up
              </span>
            </>
          ) : (
            <>
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <span
                style={styles.toggleLink}
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError(null);
                  setMessage(null);
                }}
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </span>
            </>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #E8E8E8', fontSize: '12px', color: '#A1A1AA' }}>
          <a href="https://www.zenithexpense.xyz/privacy-policy" style={{ color: '#71717A', textDecoration: 'none' }}>Privacy Policy</a>
          <span style={{ margin: '0 8px' }}>&middot;</span>
          <a href="https://www.zenithexpense.xyz/terms-of-service" style={{ color: '#71717A', textDecoration: 'none' }}>Terms of Service</a>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        input:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 2px white, 0 0 0 4px #3b82f6;
        }

        button:hover:not(:disabled) {
          opacity: 0.9;
        }

        button:active:not(:disabled) {
          transform: scale(0.99);
        }

        button[style*="backgroundColor: white"]:hover:not(:disabled) {
          background-color: #FAFAFA !important;
          border-color: #d0d0d0 !important;
        }
      `}</style>
    </div>
  );
}

export default Auth;
